'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { createBrowserStepHistory } from '../lib/navigation/browserStepHistory'
import { runAuthorizedClientAction } from '../lib/authActionGateClient'

const QuantumWalletRuntime = dynamic(() => import('./QuantumWallet'), {
  ssr: false,
})

function isBrowser() {
  return typeof window !== 'undefined'
}

export default function QuantumWalletHost() {
  const [runtime, setRuntime] = useState(null)
  const browserStepRef = useRef(null)
  const runtimeRef = useRef(runtime)
  const savedRuntimeRef = useRef(null)
  const restoringRef = useRef(false)
  const restoreSequenceRef = useRef(0)
  runtimeRef.current = runtime

  useEffect(() => {
    // Capture the stable ref object, not a stale counter value, for cleanup invalidation.
    const restoreSequenceCounter = restoreSequenceRef
    const bridge = createBrowserStepHistory({
      win: window,
      owner: 'quantum-wallet-host',
      read: () => ({ open: !!runtimeRef.current }),
      restore: (state) => {
        const sequence = ++restoreSequenceCounter.current
        restoringRef.current = true
        if (!state?.open) {
          window.dispatchEvent(new CustomEvent('quantum-wallet:request-close'))
          return
        }
        const saved = savedRuntimeRef.current
        if (!saved) return
        void runAuthorizedClientAction({
          actionKey: 'quantum-wallet-host-open',
          source: 'browser-step-forward',
          action: (accountId) => {
            if (sequence !== restoreSequenceCounter.current) return
            setRuntime({ ...saved, userKey: accountId, key: `${Date.now()}:${Math.random().toString(36).slice(2)}` })
          },
        })
      },
    })
    if (bridge.start()) browserStepRef.current = bridge
    return () => {
      ++restoreSequenceCounter.current
      bridge.dispose()
      if (browserStepRef.current === bridge) browserStepRef.current = null
    }
  }, [])
  useEffect(() => { restoringRef.current = false }, [runtime])

  const closeRuntime = useCallback(() => {
    if (!restoringRef.current) browserStepRef.current?.record({ open: false })
    setRuntime(null)
  }, [])

  const requestCloseRuntime = useCallback(() => {
    if (!isBrowser()) return
    window.dispatchEvent(new CustomEvent('quantum-wallet:request-close'))
  }, [])

  const openRuntime = useCallback((event) => {
    const detail = event?.detail || {}
    const vipActive = typeof detail.vipActive === 'boolean' ? detail.vipActive : !!detail.vip
    const eventType = String(event?.type || 'quantum-wallet:open')

    void runAuthorizedClientAction({
      actionKey: 'quantum-wallet-host-open',
      source: `${eventType}:${String(detail.source || 'wallet-trigger')}`,
      action: (accountId) => {
        if (!restoringRef.current) browserStepRef.current?.record({ open: true })
        const next = {
          key: `${Date.now()}:${Math.random().toString(36).slice(2)}`,
          userKey: accountId,
          vipActive,
        }
        savedRuntimeRef.current = next
        setRuntime(next)
      },
    })
  }, [])

  useEffect(() => {
    if (!isBrowser()) return undefined

    window.addEventListener('quantum-wallet:open', openRuntime)
    window.addEventListener('qcoin:open', openRuntime)
    window.addEventListener('quantum-wallet:close', requestCloseRuntime)

    return () => {
      window.removeEventListener('quantum-wallet:open', openRuntime)
      window.removeEventListener('qcoin:open', openRuntime)
      window.removeEventListener('quantum-wallet:close', requestCloseRuntime)
    }
  }, [openRuntime, requestCloseRuntime])

  if (!runtime) return null

  return (
    <QuantumWalletRuntime
      key={runtime.key}
      userKey={runtime.userKey}
      vipActive={runtime.vipActive}
      onClose={closeRuntime}
    />
  )
}
